import { useState, useRef, ChangeEvent, useEffect, Suspense } from "react";
import { useAtomValue } from "jotai";
import classNames from "classnames";
import { App, AppCategory } from "@zapier/types";
import { SkeletonText } from "@zapier/design-system";

import { appsByCategoryAsyncAtomFamily } from "./appsByCategoryAsyncAtomFamily";
import { useAppSearchData } from "./useAppSearchData";
import { AppList } from "./AppList";
import { popularApps, zapierApps } from "./recommendedApps";
import styles from "./AppPicker.module.css";

export function AppPickerSuspended({
  category,
  onSelect,
  onClose,
  selectedApp,
}: {
  category?: AppCategory;
  onSelect: (app: App) => void;
  onClose: () => void;
  selectedApp?: string;
}) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchInputValue, setSearchInputValue] = useState("");
  const { apps, onSearch, loading } = useAppSearchData({
    limit: 10,
    searchDebounceDelay: 300,
    excludeUpcoming: true,
    includeIsAppAdmin: true,
    includeIsAppCollaborator: true,
    apiHostname: "", // relative queries
  });

  const categoryApps = useAtomValue(appsByCategoryAsyncAtomFamily(category));

  // Add click outside handler
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    }

    // Add event listener
    document.addEventListener("mousedown", handleClickOutside);

    // Clean up
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  const onChangeApp = (service: App | null) => {
    if (service) {
      onSelect(service);
    }
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(e.target.value);
    onSearch(e.target.value);
  };

  const handleAppClick = (app: App) => {
    onChangeApp(app);
  };

  const renderApps = () => {
    if (searchInputValue) {
      return (
        <AppList
          apps={apps}
          handleAppClick={handleAppClick}
          isLoading={loading}
          title="Search results"
        />
      );
    }

    const suggestedApps = categoryApps?.length
      ? categoryApps.filter((app: App) => app.name !== selectedApp).slice(0, 7)
      : popularApps;

    return (
      <div className={styles.appGrid}>
        <AppList
          apps={suggestedApps}
          handleAppClick={handleAppClick}
          title="Suggested apps"
        />
        <AppList
          apps={zapierApps}
          handleAppClick={handleAppClick}
          title="Zapier apps"
        />
      </div>
    );
  };

  return (
    <div className={styles.appPickerContainer} ref={dropdownRef}>
      {/* Search Input */}
      <input
        type="text"
        className={styles.searchInput}
        placeholder="Search apps"
        value={searchInputValue}
        onChange={handleSearchChange}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
      />

      {/* App Selection Grid */}
      <div className={styles.appWrapper}>{renderApps()}</div>
    </div>
  );
}

function AppPickerLoader() {
  return (
    <div className={classNames(styles.appPickerContainer, styles.loading)}>
      <SkeletonText lines={3} size="body" textAlign="left" width="100%" />
    </div>
  );
}

export function AppPicker(props: {
  category?: AppCategory;
  onSelect: (app: App) => void;
  onClose: () => void;
  selectedApp?: string;
}) {
  return (
    <Suspense fallback={<AppPickerLoader />}>
      <AppPickerSuspended {...props} />
    </Suspense>
  );
}
